import { useState, type FormEvent } from 'react';
import { demander } from '@/components/ui/Fenetres';
import { Camera, ChevronLeft, ChevronRight, FileSpreadsheet, Plus, RefreshCw, X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog } from '@/components/ui/Dialog';
import { toast } from '@/stores/toastStore';
import { useAuthStore } from '@/stores/authStore';
import { exportToXLSX, buildExportFilename } from '@/utils/exports';
import { etatApi, presenceApi, type AutorisationHs, type EmployePointage, type PassageAffiche, type PresenceLigne } from '@/api/pointage';

/**
 * Pointage P3 — présence du jour (docs/PLAN-POINTAGE.md).
 * Règles (décisions PDG) : retard compté depuis l'heure de début dès que la tolérance est dépassée ; départ avant
 * l'heure signalé dès la 1re minute ; arrivée sans départ = présent mais « pointage incomplet », à corriger.
 */

export const LIBELLE_MOTIF: Record<string, string> = {
  conge: 'Congé', maladie: 'Maladie', permission: 'Permission', mission: 'Mission', autre: 'Autre',
};
const LIBELLE_SOURCE: Record<string, string> = { borne: 'borne', pointeuse: 'pointeuse', import: 'import', correction: 'saisie RH' };

const aujourdhui = () => new Date().toISOString().slice(0, 10);
const decaler = (jour: string, n: number) => { const d = new Date(`${jour}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
export const hm = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }) : '—');
const duree = (min: number | null) => (min == null ? '—' : `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`);
const jourLong = (jour: string) => new Date(`${jour}T00:00:00Z`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

/** Les badges d'une ligne : l'état principal, puis ce qui est à signaler. */
export function Etats({ l }: { l: PresenceLigne }) {
  const b: { t: string; c: string }[] = [];
  switch (l.etat) {
    case 'present':
      if (!l.retardMin && !l.departAvantMin && !l.incomplet) b.push({ t: 'Présent', c: 'ds-bd-s' });
      if (l.retardMin) b.push({ t: `Retard ${l.retardMin} min`, c: 'ds-bd-w' });
      if (l.departAvantMin) b.push({ t: `Départ ${l.departAvantMin} min avant l'heure`, c: 'ds-bd-w' });
      if (l.incomplet) b.push({ t: 'Pointage incomplet', c: 'ds-bd-d' });
      if (l.horsJourTravaille) b.push({ t: 'Jour non travaillé', c: 'ds-bd-i' });
      break;
    case 'absent': b.push({ t: 'Absent', c: 'ds-bd-d' }); break;
    case 'absence_justifiee': b.push({ t: `Absence justifiée · ${LIBELLE_MOTIF[l.motifAbsence ?? 'autre']}`, c: 'ds-bd-i' }); break;
    case 'ferie': b.push({ t: 'Jour férié', c: 'ds-bd-n' }); break;
    case 'non_travaille': b.push({ t: 'Jour non travaillé', c: 'ds-bd-n' }); break;
    case 'attendu': b.push({ t: l.jour > new Date().toISOString().slice(0, 10) ? 'À venir' : 'Pas encore arrivé', c: 'ds-bd-n' }); break;
    case 'sans_horaire': b.push({ t: 'Aucun horaire', c: 'ds-bd-n' }); break;
  }
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{b.map((x) => <span key={x.t} className={`ds-bd ${x.c}`}>{x.t}</span>)}</div>;
}

export const PresenceJour = () => {
  const role = useAuthStore((s) => s.user?.role);
  const filiale = useAuthStore((s) => s.filiale);
  const peutSaisir  = role === 'rh' || role === 'daf' || role === 'admin_holding';
  const voitPhotos  = peutSaisir || role === 'dg_filiale';
  // P4 : le DAF autorise les heures supplémentaires (décision PDG du 16/10/2026).
  const estDaf      = role === 'daf' || role === 'admin_holding';
  const qc = useQueryClient();
  const [jour, setJour] = useState(aujourdhui());
  const q = useQuery({ queryKey: ['hr', 'pointage', 'presence', jour], queryFn: () => presenceApi.jour(jour) });
  const [photo, setPhoto] = useState<{ nom: string; p: PassageAffiche } | null>(null);
  const [ajout, setAjout] = useState<{ e: EmployePointage; sens: 'entree' | 'sortie' } | null>(null);
  const [mois, setMois] = useState<EmployePointage | null>(null);
  const hs = useQuery({ queryKey: ['hr', 'pointage', 'hs', jour], queryFn: () => etatApi.heuresSup({ jour }) });
  const [autoriser, setAutoriser] = useState<{ e: EmployePointage; existante?: AutorisationHs } | null>(null);
  const rafraichir = () => qc.invalidateQueries({ queryKey: ['hr', 'pointage'] });

  const annuler = useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) => presenceApi.annulerPointage(id, motif),
    onSuccess: () => { toast.success('Pointage annulé'); void rafraichir(); },
  });
  const demanderAnnulation = async (p: PassageAffiche, nom: string) => {
    const motif = await demander.texte({ titre: `Annuler le passage de ${nom} à ${hm(p.horodatage)} ?`, libelle: 'Motif (5 caractères au moins)', min: 5, confirmer: 'Annuler le passage', danger: true });
    if (motif === null) return;
    annuler.mutate({ id: p.id, motif });
  };

  const exporter = () => {
    if (!q.data) return;
    void exportToXLSX({
      filename:  buildExportFilename(`Presence-${jour}`, filiale?.filialeKey, 'xlsx'),
      sheetName: `Présence ${jour}`,
      rows:      q.data.lignes,
      columns: [
        { header: 'Employé',   accessor: (l) => l.employe.fullName },
        { header: 'Matricule', accessor: (l) => l.employe.matricule },
        { header: 'Arrivée',   accessor: (l) => hm(l.arrivee) },
        { header: 'Départ',    accessor: (l) => hm(l.depart) },
        { header: 'État',      accessor: (l) => l.etat === 'absence_justifiee' ? `Absence justifiée (${LIBELLE_MOTIF[l.motifAbsence ?? 'autre']})` : l.etat },
        { header: 'Retard (min)', accessor: (l) => l.retardMin, align: 'right' },
        { header: 'Départ avant l’heure (min)', accessor: (l) => l.departAvantMin, align: 'right' },
        { header: 'Incomplet', accessor: (l) => (l.incomplet ? 'oui' : '') },
        { header: 'Temps de présence', accessor: (l) => duree(l.minutesTravaillees) },
      ],
    });
  };

  const c = q.data?.compteurs;
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <button className="ds-btn ds-btn-g ds-btn-sm" aria-label="Jour précédent" onClick={() => setJour(decaler(jour, -1))}><ChevronLeft size={14} /></button>
        <input type="date" className="ds-fi" style={{ width: 'auto' }} value={jour} max={aujourdhui()} onChange={(e) => e.target.value && setJour(e.target.value)} />
        <button className="ds-btn ds-btn-g ds-btn-sm" aria-label="Jour suivant" disabled={jour >= aujourdhui()} onClick={() => setJour(decaler(jour, 1))}><ChevronRight size={14} /></button>
        <span style={{ fontSize: 13, color: 'var(--tx-2)', textTransform: 'capitalize' }}>{jourLong(jour)}{q.data?.ferie ? ' — jour férié' : ''}</span>
        <span style={{ flex: 1 }} />
        <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void q.refetch()} aria-label="Rafraîchir"><RefreshCw size={14} /></button>
        <button className="ds-btn ds-btn-g ds-btn-sm" onClick={exporter} disabled={!q.data}><FileSpreadsheet size={14} /> Excel</button>
      </div>

      {c && (
        <div className="ds-kpi-strip cols-5">
          <Compteur titre="Présents" valeur={`${c.presents} / ${c.employes}`} />
          <Compteur titre="En retard" valeur={c.retards} />
          <Compteur titre="Absents" valeur={c.absents} sous={c.justifies ? `+ ${c.justifies} justifié(s)` : undefined} />
          <Compteur titre="Pointages incomplets" valeur={c.incomplets} />
          <Compteur titre="Départs avant l'heure" valeur={c.departsAvant} />
        </div>
      )}

      <div className="ds-table-panel">
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="ds-tbl" style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th>Employé</th><th style={{ width: 150 }}>Arrivée</th><th style={{ width: 150 }}>Départ</th>
                <th>État</th><th style={{ width: 100 }}>Présence</th><th style={{ width: 150 }} />
              </tr>
            </thead>
            <tbody>
              {q.isLoading && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24 }}>Chargement…</td></tr>}
              {q.data?.lignes.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Aucun employé actif</td></tr>}
              {q.data?.lignes.map((l) => {
                const nom = l.employe.fullName;
                const passage = (sens: 'entree' | 'sortie') => {
                  const iso = sens === 'entree' ? l.arrivee : l.depart;
                  const p = l.passages.find((x) => x.horodatage === iso);
                  if (!iso || !p) {
                    return peutSaisir && l.etat !== 'non_travaille' && l.etat !== 'ferie' && jour <= aujourdhui()
                      ? <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setAjout({ e: l.employe, sens })}><Plus size={12} /> {sens === 'entree' ? 'Arrivée' : 'Départ'}</button>
                      : '—';
                  }
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <strong>{hm(iso)}</strong>
                      {voitPhotos && p.photo === 'disponible' && (
                        <button className="ds-btn ds-btn-g ds-btn-sm" aria-label="Voir la photo" onClick={() => setPhoto({ nom, p })}><Camera size={12} /></button>
                      )}
                      {p.source !== 'borne' && <span className="ds-bd ds-bd-i" style={{ fontSize: 10 }} title={p.motif ?? ''}>{LIBELLE_SOURCE[p.source]}</span>}
                      {p.differe && <span className="ds-bd ds-bd-n" style={{ fontSize: 10 }} title="Enregistré sans réseau, envoyé plus tard">différé</span>}
                      {peutSaisir && (
                        <button className="ds-btn ds-btn-g ds-btn-sm" aria-label="Annuler ce pointage" title="Annuler ce pointage" onClick={() => demanderAnnulation(p, nom)}><X size={12} /></button>
                      )}
                    </div>
                  );
                };
                return (
                  <tr key={l.employe.id}>
                    <td>
                      <button className="ds-btn-link" style={{ fontWeight: 600, background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left' }}
                        onClick={() => setMois(l.employe)} title="Voir son mois">{nom}</button>
                      <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{l.employe.department}{l.employe.numeroPointage ? ` · n° ${l.employe.numeroPointage}` : ' · sans numéro'}</div>
                    </td>
                    <td>{passage('entree')}</td>
                    <td>{passage('sortie')}</td>
                    <td><Etats l={l} /></td>
                    <td>{duree(l.minutesTravaillees)}</td>
                    <td>
                      {l.passages.length > 2 && <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{l.passages.length} passages</div>}
                      {(() => {
                        const a = hs.data?.find((x) => x.employeeId === l.employe.id);
                        if (a) return <button className="ds-btn ds-btn-g ds-btn-sm" disabled={!estDaf} title={a.motif} onClick={() => setAutoriser({ e: l.employe, existante: a })}>HS autorisées {duree(a.minutes)}</button>;
                        return estDaf && l.etat === 'present' ? <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setAutoriser({ e: l.employe })}>Heures sup.</button> : null;
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {photo && <PhotoPassage nom={photo.nom} p={photo.p} onClose={() => setPhoto(null)} />}
      {ajout && <AjoutPassage jour={jour} {...ajout} onClose={() => setAjout(null)} onSaved={() => { setAjout(null); void rafraichir(); }} />}
      {mois && <MoisEmploye e={mois} moisInitial={jour.slice(0, 7)} onClose={() => setMois(null)} />}
      {autoriser && <AutoriserHs jour={jour} {...autoriser} onClose={() => setAutoriser(null)} onSaved={() => { setAutoriser(null); void rafraichir(); }} />}
    </div>
  );
};

const Compteur = ({ titre, valeur, sous }: { titre: string; valeur: number | string; sous?: string }) => (
  <div className="ds-ks">
    <div className="ds-ks-label">{titre}</div>
    <div className="ds-ks-value">{valeur}</div>
    {sous && <div className="ds-ks-sub">{sous}</div>}
  </div>
);

const PhotoPassage = ({ nom, p, onClose }: { nom: string; p: PassageAffiche; onClose: () => void }) => {
  const q = useQuery({ queryKey: ['hr', 'pointage', 'photo', p.id], queryFn: () => presenceApi.photo(p.id), staleTime: 240_000 });
  return (
    <Dialog open onClose={onClose} title={nom} subtitle={`${p.sens === 'entree' ? 'Arrivée' : p.sens === 'sortie' ? 'Départ' : 'Passage'} à ${hm(p.horodatage)}`}>
      {q.isLoading ? 'Chargement…' : q.data ? (
        <img src={q.data.url} alt={`Photo de ${nom}`} style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 }} />
      ) : 'Photo indisponible.'}
    </Dialog>
  );
};

const AjoutPassage = ({ jour, e, sens, onClose, onSaved }: {
  jour: string; e: EmployePointage; sens: 'entree' | 'sortie'; onClose: () => void; onSaved: () => void;
}) => {
  const [heure, setHeure] = useState(sens === 'entree' ? '07:30' : '17:30');
  const [motif, setMotif] = useState('');
  const m = useMutation({
    mutationFn: () => presenceApi.corriger({ employeeId: e.id, horodatage: `${jour}T${heure}:00Z`, sens, motif: motif.trim() }),
    onSuccess: () => { toast.success(`${sens === 'entree' ? 'Arrivée' : 'Départ'} ajouté(e)`, `${e.fullName} à ${heure}`); onSaved(); },
  });
  const soumettre = (ev: FormEvent) => { ev.preventDefault(); if (motif.trim().length >= 5) m.mutate(); };
  return (
    <Dialog open onClose={onClose} title={`Ajouter ${sens === 'entree' ? 'l’arrivée' : 'le départ'} — ${e.fullName}`}
      subtitle={`Le ${new Date(`${jour}T00:00:00Z`).toLocaleDateString('fr-FR', { timeZone: 'UTC' })}. La saisie restera marquée « saisie RH ».`}>
      <form onSubmit={soumettre} style={{ display: 'grid', gap: 12 }}>
        <label style={{ fontSize: 12 }}>Heure<input type="time" className="ds-fi" value={heure} onChange={(ev) => setHeure(ev.target.value)} required /></label>
        <label style={{ fontSize: 12 }}>Motif (obligatoire)
          <input className="ds-fi" value={motif} onChange={(ev) => setMotif(ev.target.value)} minLength={5} maxLength={300} required
            placeholder="Ex. oubli de pointage, arrivée constatée par le chef de production" />
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={m.isPending || motif.trim().length < 5}>Enregistrer</button>
        </div>
      </form>
    </Dialog>
  );
};

const MoisEmploye = ({ e, moisInitial, onClose }: { e: EmployePointage; moisInitial: string; onClose: () => void }) => {
  const [mois, setMois] = useState(moisInitial);
  const q = useQuery({ queryKey: ['hr', 'pointage', 'mois', e.id, mois], queryFn: () => presenceApi.mois(e.id, mois) });
  const jours = q.data?.jours ?? [];
  const total = (f: (l: PresenceLigne) => number) => jours.reduce((s, l) => s + f(l), 0);
  return (
    <Dialog open onClose={onClose} size="lg" title={e.fullName} subtitle="Le mois jour par jour">
      <div style={{ display: 'grid', gap: 12 }}>
        <input type="month" className="ds-fi" style={{ width: 'auto' }} value={mois} onChange={(ev) => ev.target.value && setMois(ev.target.value)} />
        {q.data && (
          <div style={{ fontSize: 13, display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <span>Jours présents : <strong>{jours.filter((l) => l.etat === 'present').length}</strong></span>
            <span>Absences non justifiées : <strong>{jours.filter((l) => l.etat === 'absent').length}</strong></span>
            <span>Absences justifiées : <strong>{jours.filter((l) => l.etat === 'absence_justifiee').length}</strong></span>
            <span>Retards : <strong>{total((l) => l.retardMin)} min</strong></span>
            <span>Pointages incomplets : <strong>{jours.filter((l) => l.incomplet).length}</strong></span>
          </div>
        )}
        <div style={{ overflowX: 'auto', maxHeight: '60vh' }}>
          <table className="ds-tbl" style={{ minWidth: 560 }}>
            <thead><tr><th style={{ width: 110 }}>Jour</th><th style={{ width: 70 }}>Arrivée</th><th style={{ width: 70 }}>Départ</th><th>État</th><th style={{ width: 90 }}>Présence</th></tr></thead>
            <tbody>
              {q.isLoading && <tr><td colSpan={5}>Chargement…</td></tr>}
              {jours.map((l) => (
                <tr key={l.jour}>
                  <td style={{ textTransform: 'capitalize' }}>{new Date(`${l.jour}T00:00:00Z`).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'UTC' })}</td>
                  <td>{hm(l.arrivee)}</td><td>{hm(l.depart)}</td><td><Etats l={l} /></td><td>{duree(l.minutesTravaillees)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Dialog>
  );
};

/** P4 — autorisation d'heures supplémentaires par le DAF : employé, jour, durée maximale, motif. */
const AutoriserHs = ({ jour, e, existante, onClose, onSaved }: {
  jour: string; e: EmployePointage; existante?: AutorisationHs; onClose: () => void; onSaved: () => void;
}) => {
  const [heures, setHeures] = useState('1');
  const [minutes, setMinutes] = useState('0');
  const [motif, setMotif] = useState('');
  const total = Number(heures || 0) * 60 + Number(minutes || 0);
  const creer = useMutation({
    mutationFn: () => etatApi.autoriserHs({ employeeId: e.id, jour, minutes: total, motif: motif.trim() }),
    onSuccess: () => { toast.success('Heures supplémentaires autorisées', `${e.fullName} : ${duree(total)}`); onSaved(); },
  });
  const annuler = useMutation({
    mutationFn: (m: string) => etatApi.annulerHs(existante!.id, m),
    onSuccess: () => { toast.success('Autorisation annulée'); onSaved(); },
  });
  return (
    <Dialog open onClose={onClose} title={`Heures supplémentaires — ${e.fullName}`}
      subtitle="Comptées seulement après 17 h 30 (ou un jour de repos), dans la limite autorisée ici.">
      {existante ? (
        <div style={{ display: 'grid', gap: 12, fontSize: 13 }}>
          <div>Autorisées ce jour : <strong>{duree(existante.minutes)}</strong> — {existante.motif}</div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="ds-btn ds-btn-g" onClick={onClose}>Fermer</button>
            <button className="ds-btn ds-btn-g" disabled={annuler.isPending} onClick={async () => {
              const m = await demander.texte({ titre: 'Annuler l’autorisation', libelle: 'Motif de l’annulation (5 caractères au moins)', min: 5, confirmer: 'Annuler l’autorisation', danger: true });
              if (m !== null) annuler.mutate(m);
            }}>Annuler l'autorisation</button>
          </div>
        </div>
      ) : (
        <form onSubmit={(ev) => { ev.preventDefault(); if (total > 0 && motif.trim().length >= 5) creer.mutate(); }} style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <label style={{ fontSize: 12 }}>Heures<input type="number" min={0} max={16} className="ds-fi" style={{ width: 80 }} value={heures} onChange={(ev) => setHeures(ev.target.value)} /></label>
            <label style={{ fontSize: 12 }}>Minutes<input type="number" min={0} max={59} step={5} className="ds-fi" style={{ width: 80 }} value={minutes} onChange={(ev) => setMinutes(ev.target.value)} /></label>
          </div>
          <label style={{ fontSize: 12 }}>Motif (obligatoire)
            <input className="ds-fi" value={motif} onChange={(ev) => setMotif(ev.target.value)} minLength={5} maxLength={300} required placeholder="Ex. chargement d'une commande urgente" />
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
            <button type="submit" className="ds-btn ds-btn-p" disabled={creer.isPending || total <= 0 || total > 960 || motif.trim().length < 5}>Autoriser</button>
          </div>
        </form>
      )}
    </Dialog>
  );
};
